// store/slices/novuSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk for initializing Novu notifications
export const initializeNovuNotifications = createAsyncThunk(
  'novu/initialize',
  async (userId, { rejectWithValue }) => {
    try {
      // This will be handled by the NovuProvider component
      // We just track the initialization state here
      return { userId, initialized: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for syncing with existing notifications
export const syncWithExistingNotifications = createAsyncThunk(
  'novu/syncExisting',
  async (existingNotifications, { rejectWithValue }) => {
    try {
      // Transform existing MongoDB notifications to match Novu format
      const transformedNotifications = existingNotifications.map(notification => ({
        id: notification._id,
        content: notification.Notification_body,
        title: notification.Notification_title,
        category: notification.category,
        priority: notification.priority,
        read: notification.read,
        createdAt: notification.createdAt,
        readAt: notification.readAt,
        userType: notification.UserType,
        userId: notification.userid,
        data: notification.data
      }));

      return transformedNotifications;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const novuSlice = createSlice({
  name: 'novu',
  initialState: {
    isInitialized: false,
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    socketConnected: false,
    lastSyncTimestamp: null,
    preferences: {
      inApp: true,
      email: false,
      push: true
    }
  },
  reducers: {
    /**
     * Sets in `setSocketConnected`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    setSocketConnected: (state, action) => {
      state.socketConnected = action.payload;
    },
    
    /**
     * Adds in `addNotification`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    addNotification: (state, action) => {
      const notification = action.payload;
      const exists = state.notifications.find(n => n.id === notification.id);
      
      if (!exists) {
        state.notifications.unshift(notification);
        if (!notification.read) {
          state.unreadCount += 1;
        }
      }
    },
    
    /**
     * Updates in `updateNotification`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    updateNotification: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.notifications.findIndex(n => n.id === id);
      
      if (index !== -1) {
        const wasUnread = !state.notifications[index].read;
        state.notifications[index] = { ...state.notifications[index], ...updates };
        
        // Update unread count if read status changed
        if (wasUnread && updates.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        } else if (!wasUnread && updates.read === false) {
          state.unreadCount += 1;
        }
      }
    },
    
    /**
     * Executes logic in `markNotificationAsRead`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    markNotificationAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);
      
      if (notification && !notification.read) {
        notification.read = true;
        notification.readAt = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    
    /**
     * Executes logic in `markAllAsRead`.
     * @param {any} state Redux state.
     * @returns {void} No return value.
     */
    markAllAsRead: (state) => {
      state.notifications = state.notifications.map(notification => ({
        ...notification,
        read: true,
        readAt: notification.readAt || new Date().toISOString()
      }));
      state.unreadCount = 0;
    },
    
    /**
     * Sets in `setUnreadCount`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    
    /**
     * Removes in `removeNotification`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      const index = state.notifications.findIndex(n => n.id === notificationId);
      
      if (index !== -1) {
        const wasUnread = !state.notifications[index].read;
        state.notifications.splice(index, 1);
        if (wasUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },
    
    /**
     * Executes logic in `clearNotifications`.
     * @param {any} state Redux state.
     * @returns {void} No return value.
     */
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    
    /**
     * Updates in `updatePreferences`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    updatePreferences: (state, action) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },
    
    /**
     * Sets in `setError`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    
    /**
     * Executes logic in `clearError`.
     * @param {any} state Redux state.
     * @returns {void} No return value.
     */
    clearError: (state) => {
      state.error = null;
    },
    
    /**
     * Sets in `setLoading`.
     * @param {any} state Redux state.
     * @param {any} action Redux action.
     * @returns {void} No return value.
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    /**
     * Sets in `setSyncTimestamp`.
     * @param {any} state Redux state.
     * @returns {void} No return value.
     */
    setSyncTimestamp: (state) => {
      state.lastSyncTimestamp = new Date().toISOString();
    }
  },
  /**
   * Executes logic in `extraReducers`.
   * @param {any} builder Input value.
   * @returns {void} No return value.
   */
  extraReducers: (builder) => {
    builder
      .addCase(initializeNovuNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeNovuNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.error = null;
      })
      .addCase(initializeNovuNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isInitialized = false;
      })
      .addCase(syncWithExistingNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(syncWithExistingNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = [...action.payload, ...state.notifications];
        state.unreadCount = action.payload.filter(n => !n.read).length;
        state.lastSyncTimestamp = new Date().toISOString();
        state.error = null;
      })
      .addCase(syncWithExistingNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setSocketConnected,
  addNotification,
  updateNotification,
  markNotificationAsRead,
  markAllAsRead,
  setUnreadCount,
  removeNotification,
  clearNotifications,
  updatePreferences,
  setError,
  clearError,
  setLoading,
  setSyncTimestamp
} = novuSlice.actions;

// Selectors
/**
 * Executes logic in `selectNovuNotifications`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuNotifications = (state) => state.novu.notifications;
/**
 * Executes logic in `selectNovuUnreadCount`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuUnreadCount = (state) => state.novu.unreadCount;
/**
 * Executes logic in `selectNovuIsInitialized`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuIsInitialized = (state) => state.novu.isInitialized;
/**
 * Executes logic in `selectNovuIsLoading`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuIsLoading = (state) => state.novu.isLoading;
/**
 * Executes logic in `selectNovuError`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuError = (state) => state.novu.error;
/**
 * Executes logic in `selectNovuSocketConnected`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuSocketConnected = (state) => state.novu.socketConnected;
/**
 * Executes logic in `selectNovuPreferences`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuPreferences = (state) => state.novu.preferences;
/**
 * Executes logic in `selectLastSyncTimestamp`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectLastSyncTimestamp = (state) => state.novu.lastSyncTimestamp;

// Enhanced selectors
/**
 * Executes logic in `selectUnreadNovuNotifications`.
 * @param {any} state Redux state.
 * @returns {any} Returns a value of type `any`.
 */
export const selectUnreadNovuNotifications = (state) =>
  state.novu.notifications.filter(notification => !notification.read);

/**
 * Executes logic in `selectNovuNotificationsByCategory`.
 * @param {any} category Input value.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuNotificationsByCategory = (category) => (state) =>
  state.novu.notifications.filter(notification => notification.category === category);

/**
 * Executes logic in `selectNovuNotificationsByPriority`.
 * @param {any} priority Input value.
 * @returns {any} Returns a value of type `any`.
 */
export const selectNovuNotificationsByPriority = (priority) => (state) =>
  state.novu.notifications.filter(notification => notification.priority === priority);

/**
 * Executes logic in `selectRecentNovuNotifications`.
 * @param {number} hours Input value. Default: 24.
 * @returns {any} Returns a value of type `any`.
 */
export const selectRecentNovuNotifications = (hours = 24) => (state) => {
  const cutoff = new Date();
  cutoff.setHours(cutoff.getHours() - hours);
  return state.novu.notifications.filter(
    notification => new Date(notification.createdAt) >= cutoff
  );
};

export default novuSlice.reducer;